/**
 * PlanCreatePage.jsx
 * Parent-facing plan creation wizard.
 *
 * Steps:
 *   "settings" — Name, Icon picker, Auto vs Manual (create only), word count (Auto+create only)
 *   "picker"   — Word selection
 *   "quiz"     — Quiz count slider + Save
 *
 * Picker layout:
 *   Mobile  (< lg)  : selected-words horizontal strip at top  + stable word list with +/✓ buttons
 *   Web/Tab (≥ lg)  : left word list  |  right selected-words panel (320px)
 *   Words never move in the list — + turns to ✓ in place.
 *
 * Edit mode changes:
 *   - Auto/Manual selector hidden — edit always goes through the word picker
 *   - Pre-selects existing plan.word_ids regardless of original mode
 *   - PATCH always sends mode:"manual" + word_ids (no word_count)
 *   - word-picker uses exclude_plan_id so own words stay available
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Sparkle, Check, Plus, X,
  MagnifyingGlass, Warning,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomInput }  from "@/components/styleguide/ui/BloomInput";
import { cn }          from "@/lib/utils";
import { api }         from "./api";
import { PLAN_ICON_CONFIG, ICON_KEYS, iconConfig } from "./planIcons";

/* ── Difficulty badge colours ────────────────────────────────────────────── */
const DIFF_CLS = {
  easy:   "bg-teal/15 text-teal-ink",
  medium: "bg-warning/15 text-warning-ink",
  hard:   "bg-primary/10 text-primary-strong",
};

/* ── Section header ─────────────────────────────────────────────────────── */
function SectionHeader({ label, count }) {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 px-6 py-2.5 backdrop-blur">
      <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      {count != null && (
        <p className="text-[10px] font-bold text-muted-foreground">{count}</p>
      )}
    </div>
  );
}

/* ── Word preview modal ─────────────────────────────────────────────────── */
function WordPreviewModal({ word, onClose }) {
  if (!word) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-3xl border border-border bg-card shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pb-4 pt-6">
          <div className="min-w-0 flex-1">
            <p className="font-display text-2xl font-black capitalize text-foreground">{word.word}</p>
            {word.difficulty && (
              <span className={cn(
                "mt-2 inline-flex rounded-full px-3 py-1 text-xs font-bold",
                DIFF_CLS[word.difficulty] ?? "bg-muted text-muted-foreground",
              )}>
                {word.difficulty}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 pb-6">
          {word.meaning && (
            <div>
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                Meaning
              </p>
              <p className="text-sm font-semibold leading-relaxed text-foreground">{word.meaning}</p>
            </div>
          )}
          {word.example && (
            <div>
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                Example
              </p>
              <p className="text-sm font-medium italic leading-relaxed text-muted-foreground">
                "{word.example}"
              </p>
            </div>
          )}
          {word.example_context && (
            <div>
              <p className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                Context
              </p>
              <p className="text-sm font-medium leading-relaxed text-muted-foreground">
                {word.example_context}
              </p>
            </div>
          )}
          {word.is_parent_request && (
            <div className="rounded-xl bg-primary/8 px-4 py-3">
              <p className="text-xs font-bold text-primary-strong">✦ Your request — you added this word</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Word row — stable position, + / ✓ toggle button ────────────────────── */
function WordRowPlus({ word, selected, onToggle, onPreview, planNames = [] }) {
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const TAG_MAX = 2;
  const visibleTags  = tagsExpanded ? planNames : planNames.slice(0, TAG_MAX);
  const overflowCount = planNames.length - TAG_MAX;

  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b border-border px-6 py-3.5 last:border-0 transition-colors",
        selected && "bg-success/10",
      )}
    >
      {/* Clickable word info — opens preview modal */}
      <button
        onClick={() => onPreview?.(word)}
        className="min-w-0 flex-1 text-left"
      >
        {/* Word name + difficulty + plan tags, all on same line (wrap if needed) */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span className="font-display text-sm font-extrabold capitalize text-foreground">
            {word.word}
            {word.is_parent_request && (
              <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-primary-strong">
                ✦ yours
              </span>
            )}
          </span>
          {word.difficulty && (
            <span className={cn(
              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold",
              DIFF_CLS[word.difficulty] ?? "bg-muted text-muted-foreground",
            )}>
              {word.difficulty}
            </span>
          )}
          {/* Plan membership tags */}
          {visibleTags.map((planName, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-200"
            >
              {planName}
            </span>
          ))}
          {/* "+N more" overflow button */}
          {!tagsExpanded && overflowCount > 0 && (
            <button
              onClick={e => { e.stopPropagation(); setTagsExpanded(true); }}
              className="text-[10px] font-bold text-primary-strong underline-offset-1 hover:underline"
            >
              +{overflowCount} more
            </button>
          )}
        </div>
        {/* Overflow tags on next row when expanded */}
        {tagsExpanded && overflowCount > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {planNames.slice(TAG_MAX).map((planName, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-200"
              >
                {planName}
              </span>
            ))}
            <button
              onClick={e => { e.stopPropagation(); setTagsExpanded(false); }}
              className="text-[10px] font-semibold text-muted-foreground hover:text-foreground"
            >
              less
            </button>
          </div>
        )}
        {/* Meaning — one line, truncated */}
        {word.meaning && (
          <p className="mt-0.5 truncate text-xs font-semibold text-muted-foreground">
            {word.meaning}
          </p>
        )}
      </button>

      {/* Toggle button */}
      <button
        onClick={() => onToggle(word)}
        aria-label={selected ? `Remove ${word.word}` : `Add ${word.word}`}
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition",
          selected
            ? "border-success bg-success text-white"
            : "border-border bg-card text-muted-foreground hover:border-primary hover:text-primary",
        )}
      >
        {selected
          ? <Check size={14} weight="bold" />
          : <Plus  size={14} weight="bold" />
        }
      </button>
    </div>
  );
}

/* ── Mobile: selected words horizontal chip strip ────────────────────────── */
function SelectedStrip({ words, total, onRemove, onSeeAll }) {
  if (total === 0) {
    return (
      <div className="shrink-0 border-b border-border bg-muted/20 px-4 py-3">
        <p className="text-xs font-semibold text-muted-foreground">
          Tap <span className="font-bold text-foreground">+</span> on any word to add it to this plan
        </p>
      </div>
    );
  }
  const visible = words.slice(0, 6);
  const overflow = total - visible.length;
  return (
    <div className="shrink-0 border-b border-border bg-success/8 px-4 py-2.5">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-success-ink">
          {total} word{total !== 1 ? "s" : ""} in plan
        </p>
        {overflow > 0 && (
          <button
            onClick={onSeeAll}
            className="text-[10px] font-bold text-primary-strong underline-offset-2 hover:underline"
          >
            See all
          </button>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {visible.map(w => (
          <button
            key={w.word_id}
            onClick={() => onRemove(w)}
            className="flex shrink-0 items-center gap-1 rounded-full bg-success/20 px-2.5 py-1 text-xs font-bold capitalize text-success-ink transition hover:bg-success/30"
          >
            {w.word} <X size={9} weight="bold" />
          </button>
        ))}
        {overflow > 0 && (
          <button
            onClick={onSeeAll}
            className="flex shrink-0 items-center rounded-full bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground"
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Mobile: "See all selected" full-screen sheet ────────────────────────── */
function SeeAllSheet({ open, words, onRemove, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <p className="font-display text-base font-black text-foreground">
          Selected words ({words.length})
        </p>
        <button
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {words.map(w => (
          <div key={w.word_id} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-0">
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-bold capitalize text-foreground truncate">{w.word}</p>
              {w.meaning && (
                <p className="truncate text-xs text-muted-foreground">{w.meaning}</p>
              )}
            </div>
            <button
              onClick={() => onRemove(w)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-destructive"
            >
              <X size={13} weight="bold" />
            </button>
          </div>
        ))}
        {words.length === 0 && (
          <p className="px-4 py-10 text-center text-sm font-semibold text-muted-foreground">No words selected yet.</p>
        )}
      </div>
      <div className="shrink-0 border-t border-border px-4 py-3">
        <BloomButton className="w-full justify-center" onClick={onClose}>Done</BloomButton>
      </div>
    </div>
  );
}

/* ── Web: selected words right panel ────────────────────────────────────── */
function SelectedPanel({ words, total, name, onRemove, onContinue, error }) {
  return (
    <div className="flex w-[340px] xl:w-[400px] shrink-0 flex-col border-l-2 border-border bg-muted/30">
      {/* Header */}
      <div className="shrink-0 border-b border-border bg-card px-6 py-5">
        <p className="font-display text-xl font-black text-foreground">
          {total > 0 ? `${total} word${total !== 1 ? "s" : ""} selected` : "No words yet"}
        </p>
        <p className="mt-1 truncate text-sm font-semibold text-muted-foreground">{name}</p>
      </div>

      {/* Word list */}
      <div className="flex-1 overflow-y-auto">
        {words.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <p className="text-sm font-semibold leading-relaxed text-muted-foreground">
              Tap <span className="font-bold text-foreground">+</span> on any word<br />to add it to this plan
            </p>
          </div>
        ) : (
          words.map(w => (
            <div
              key={w.word_id}
              className="flex items-start gap-4 border-b border-border bg-card px-6 py-4 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-black capitalize text-foreground">{w.word}</p>
                {w.meaning && (
                  <p className="mt-0.5 line-clamp-2 text-sm font-medium leading-snug text-muted-foreground">
                    {w.meaning}
                  </p>
                )}
              </div>
              <button
                onClick={() => onRemove(w)}
                aria-label={`Remove ${w.word}`}
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
              >
                <X size={13} weight="bold" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-border bg-card px-6 py-5">
        {error && (
          <p className="mb-3 text-center text-sm font-bold text-destructive">{error}</p>
        )}
        <BloomButton
          className="w-full justify-center"
          rightIcon={total > 0 ? ArrowRight : undefined}
          disabled={total < 1}
          onClick={onContinue}
        >
          {total > 0 ? "Set quiz length" : "Add words to continue"}
        </BloomButton>
      </div>
    </div>
  );
}

/* ── Sort toggle ────────────────────────────────────────────────────────── */
function SortToggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 rounded-full bg-muted p-0.5">
      <button
        onClick={() => onChange("alpha")}
        className={cn(
          "rounded-full px-3 py-1 text-[11px] font-bold transition",
          value === "alpha"
            ? "bg-card text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        A–Z
      </button>
      <button
        onClick={() => onChange("recent")}
        className={cn(
          "rounded-full px-3 py-1 text-[11px] font-bold transition",
          value === "recent"
            ? "bg-card text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Recent
      </button>
    </div>
  );
}

/* ── Step indicator ─────────────────────────────────────────────────────── */
function StepDots({ step, mode, isEdit }) {
  const steps = (isEdit || mode === "manual")
    ? ["settings", "picker", "quiz"]
    : ["settings", "quiz"];
  return (
    <div className="flex items-center justify-center gap-2 py-2">
      {steps.map((s) => (
        <div
          key={s}
          className={cn(
            "h-2 rounded-full transition-all",
            s === step ? "w-6 bg-primary" : "w-2 bg-border",
          )}
        />
      ))}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────── */
export default function PlanCreatePage() {
  const { childId, planId } = useParams();
  const navigate             = useNavigate();
  const isEdit               = !!planId;

  const [child,       setChild]       = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(isEdit);

  // Settings step
  const [name,      setName]      = useState("");
  const [icon,      setIcon]      = useState("star");
  const [mode,      setMode]      = useState("auto");
  const [wordCount, setWordCount] = useState(15);

  // Picker step
  const [pickerData,    setPickerData]    = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [search,        setSearch]        = useState("");
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [selectedMap,   setSelectedMap]   = useState({}); // word_id → word object
  const [showSeeAll,    setShowSeeAll]    = useState(false);
  const [previewWord,   setPreviewWord]   = useState(null); // word being previewed in modal
  const [sort,          setSort]          = useState("alpha"); // "alpha" | "recent"
  const [childPlans,    setChildPlans]    = useState([]);   // all plans for this child (for plan-tag display)

  // Quiz step
  const [quizCount, setQuizCount] = useState(20);

  // Flow
  const [step,   setStep]   = useState("settings");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const searchTimer = useRef(null);

  /* Fetch child details */
  useEffect(() => {
    api("/children")
      .then(list => setChild(list.find(c => c.child_id === childId) ?? null))
      .catch(() => {});
  }, [childId]);

  /* Fetch child's plans so we can show plan-membership tags on word rows */
  useEffect(() => {
    api(`/children/${childId}/plans`)
      .then(data => setChildPlans(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [childId]);

  /* Returns the names of plans (other than the one being edited) that contain this word */
  const getWordPlanNames = useCallback((wordId) => {
    return childPlans
      .filter(p => p.plan_id !== planId && Array.isArray(p.word_ids) && p.word_ids.includes(wordId))
      .map(p => p.name);
  }, [childPlans, planId]);

  /* Load existing plan when editing */
  useEffect(() => {
    if (!isEdit) return;
    setLoadingPlan(true);
    api(`/children/${childId}/plans/${planId}`)
      .then(plan => {
        setName(plan.name ?? "");
        setIcon(plan.icon ?? "star");
        setMode(plan.mode ?? "auto");
        const ids = new Set(plan.word_ids ?? []);
        setSelectedIds(ids);
        setQuizCount(plan.quiz_count ?? 20);
      })
      .catch(() => setError("Couldn't load plan details. Refresh to try again."))
      .finally(() => setLoadingPlan(false));
  }, [childId, planId, isEdit]);

  /* When pickerData loads, populate selectedMap for pre-selected words (edit mode) */
  useEffect(() => {
    if (!pickerData) return;
    const allWords = [
      ...(pickerData.parent_requests ?? []),
      ...(pickerData.recently_added  ?? []),
      ...(pickerData.all_words       ?? []),
    ];
    setSelectedMap(prev => {
      const next = { ...prev };
      for (const w of allWords) {
        if (selectedIds.has(w.word_id) && !next[w.word_id]) {
          next[w.word_id] = w;
        }
      }
      return next;
    });
  }, [pickerData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Load word picker data */
  const loadPicker = useCallback(async (q = "") => {
    setPickerLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (isEdit && planId) params.set("exclude_plan_id", planId);
      params.set("sort", sort);
      const qs = params.toString();
      const url = `/children/${childId}/plans/word-picker${qs ? `?${qs}` : ""}`;
      setPickerData(await api(url));
    } catch {
      setPickerData({ parent_requests: [], recently_added: [], all_words: [], total: 0 });
    } finally {
      setPickerLoading(false);
    }
  }, [childId, isEdit, planId, sort]);

  useEffect(() => {
    if (step !== "picker") return;
    clearTimeout(searchTimer.current);
    if (!pickerData && !search) { loadPicker(""); return; }
    searchTimer.current = setTimeout(() => loadPicker(search), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, step, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === "picker" && !pickerData) loadPicker("");
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Toggle a word in/out of selection */
  function toggleWord(word) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(word.word_id)) {
        next.delete(word.word_id);
        setSelectedMap(m => { const n = { ...m }; delete n[word.word_id]; return n; });
      } else {
        next.add(word.word_id);
        setSelectedMap(m => ({ ...m, [word.word_id]: word }));
      }
      return next;
    });
  }

  /* Computed word count for quiz range */
  const finalWordCount = (isEdit || mode === "manual") ? selectedIds.size : wordCount;
  const minQ = Math.max(1, finalWordCount);
  const maxQ = finalWordCount * 2;

  /* Advance from settings */
  function goFromSettings() {
    setError("");
    if (!name.trim()) { setError("Give this plan a name first."); return; }
    if (isEdit || mode === "manual") {
      setStep("picker");
    } else {
      setQuizCount(Math.round(wordCount * 1.5));
      setStep("quiz");
    }
  }

  /* Advance from picker to quiz */
  function goFromPicker() {
    if (selectedIds.size < 1) { setError("Select at least one word."); return; }
    setError("");
    setQuizCount(Math.round(selectedIds.size * 1.5));
    setStep("quiz");
  }

  /* Back navigation */
  function goBack() {
    if (step === "picker") { setStep("settings"); return; }
    if (step === "quiz") {
      setStep((isEdit || mode === "manual") ? "picker" : "settings");
      return;
    }
    navigate(`/vocab/parent/child/${childId}/plans`);
  }

  /* Create or update the plan */
  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        name:       name.trim(),
        icon,
        quiz_count: Math.max(minQ, Math.min(maxQ, quizCount)),
      };
      if (isEdit) {
        body.mode     = "manual";
        body.word_ids = [...selectedIds];
      } else if (mode === "auto") {
        body.mode       = "auto";
        body.word_count = wordCount;
      } else {
        body.mode     = "manual";
        body.word_ids = [...selectedIds];
      }

      if (isEdit) {
        await api(`/children/${childId}/plans/${planId}`, { method: "PATCH", body });
      } else {
        await api(`/children/${childId}/plans`, { method: "POST", body });
      }
      navigate(`/vocab/parent/child/${childId}/plans`);
    } catch (e) {
      setError(e.message || `Couldn't ${isEdit ? "update" : "create"} plan. Please try again.`);
    } finally {
      setSaving(false);
    }
  }

  const firstName    = child?.first_name ?? "…";
  const selectedWords = Object.values(selectedMap).filter(w => selectedIds.has(w.word_id));

  /* Step-aware header labels */
  const stepLabel =
    step === "settings" ? (isEdit ? "Rename your plan"  : "Name your plan") :
    step === "picker"   ? "Choose words" :
    step === "quiz"     ? "Quiz settings" :
    (isEdit ? "Edit plan" : "Create a plan");

  const stepDesc =
    step === "picker" && selectedIds.size > 0
      ? `${selectedIds.size} word${selectedIds.size !== 1 ? "s" : ""} selected · For ${firstName}`
      : step === "quiz"
      ? `${name} · For ${firstName}`
      : `For ${firstName}`;

  /* ── Word list: handles search-merged view vs sectioned view ─────────── */
  const renderWordList = () => {
    if (pickerLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm font-semibold text-muted-foreground">Loading words…</p>
        </div>
      );
    }
    if (!pickerData) return null;

    /* ── Search mode: merge filtered parent_requests + backend all_words ── */
    if (search) {
      const q = search.toLowerCase();
      const filteredParent = (pickerData.parent_requests ?? []).filter(w =>
        w.word.toLowerCase().includes(q) || w.meaning?.toLowerCase().includes(q)
      );
      const seen = new Set(filteredParent.map(w => w.word_id));
      const combined = [
        ...filteredParent,
        ...(pickerData.all_words ?? []).filter(w => !seen.has(w.word_id)),
      ];
      return (
        <div>
          <SectionHeader label={`Results for "${search}"`} count={combined.length} />
          {combined.length === 0 && (
            <p className="px-4 py-8 text-center text-sm font-semibold text-muted-foreground">
              No words match your search.
            </p>
          )}
          {combined.map(w => (
            <WordRowPlus key={w.word_id} word={w} selected={selectedIds.has(w.word_id)} onToggle={toggleWord} onPreview={setPreviewWord} planNames={getWordPlanNames(w.word_id)} />
          ))}
        </div>
      );
    }

    /* ── No search — show all sections ── */
    return (
      <>
        {pickerData.parent_requests?.length > 0 && (
          <div>
            <SectionHeader label="Your requests" count={pickerData.parent_requests.length} />
            {pickerData.parent_requests.map(w => (
              <WordRowPlus key={w.word_id} word={w} selected={selectedIds.has(w.word_id)} onToggle={toggleWord} onPreview={setPreviewWord} planNames={getWordPlanNames(w.word_id)} />
            ))}
          </div>
        )}
        {pickerData.recently_added?.length > 0 && (
          <div>
            <SectionHeader label="Recently added" count={pickerData.recently_added.length} />
            {pickerData.recently_added.map(w => (
              <WordRowPlus key={w.word_id} word={w} selected={selectedIds.has(w.word_id)} onToggle={toggleWord} onPreview={setPreviewWord} planNames={getWordPlanNames(w.word_id)} />
            ))}
          </div>
        )}
        <div>
          <SectionHeader label="All words" count={pickerData.total} />
          {pickerData.all_words?.length === 0 && (
            <p className="px-4 py-8 text-center text-sm font-semibold text-muted-foreground">
              No words available.
            </p>
          )}
          {pickerData.all_words?.map(w => (
            <WordRowPlus key={w.word_id} word={w} selected={selectedIds.has(w.word_id)} onToggle={toggleWord} onPreview={setPreviewWord} planNames={getWordPlanNames(w.word_id)} />
          ))}
        </div>
      </>
    );
  };

  /* ── Step: Settings ─────────────────────────────────────────────────── */
  const renderSettings = () => (
    <div className="space-y-6 px-4 py-6">

      {/* Name */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-extrabold text-foreground">Plan name</p>
          <span className={cn(
            "text-xs font-bold tabular-nums",
            name.length >= 20 ? "text-primary-strong" : "text-muted-foreground",
          )}>
            {name.length}/20
          </span>
        </div>
        <BloomInput
          placeholder="e.g. Week 1"
          value={name}
          onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9 ()&\-:]/g, "").slice(0, 20))}
          maxLength={20}
          autoFocus
        />
        <p className="mt-1.5 text-[11px] font-semibold text-muted-foreground">
          Letters, numbers and ( ) &amp; - : allowed · max 20 characters
        </p>
      </div>

      {/* Icon picker */}
      <div>
        <p className="mb-3 text-sm font-extrabold text-foreground">Choose an icon</p>
        <div className="grid grid-cols-5 gap-2">
          {ICON_KEYS.map(key => {
            const { Icon, bg, text } = PLAN_ICON_CONFIG[key];
            const active = icon === key;
            return (
              <button
                key={key}
                onClick={() => setIcon(key)}
                aria-label={key}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-2xl py-3 transition",
                  active ? "bg-primary shadow-soft" : "bg-muted/60 hover:bg-muted",
                )}
              >
                <Icon size={22} weight="fill" className={active ? "text-white" : text} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Mode selector — hidden in edit */}
      {!isEdit && (
        <div>
          <p className="mb-3 text-sm font-extrabold text-foreground">How do you want to pick words?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMode("auto")}
              className={cn(
                "rounded-2xl border-2 p-4 text-left transition",
                mode === "auto" ? "border-secondary bg-secondary/10" : "border-border bg-card hover:bg-muted/30",
              )}
            >
              <Sparkle size={22} weight="fill" className={mode === "auto" ? "text-secondary-ink" : "text-muted-foreground"} />
              <p className="mt-2 font-display text-sm font-black text-foreground">Auto</p>
              <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
                Bloom picks the best new words for {firstName}
              </p>
            </button>
            <button
              onClick={() => setMode("manual")}
              className={cn(
                "rounded-2xl border-2 p-4 text-left transition",
                mode === "manual" ? "border-secondary bg-secondary/10" : "border-border bg-card hover:bg-muted/30",
              )}
            >
              <Check size={22} weight="bold" className={mode === "manual" ? "text-secondary-ink" : "text-muted-foreground"} />
              <p className="mt-2 font-display text-sm font-black text-foreground">Manual</p>
              <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
                I'll choose the words myself
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Auto word count slider — hidden in edit */}
      {!isEdit && mode === "auto" && (
        <div className="rounded-2xl border border-border bg-muted/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-extrabold text-foreground">How many words?</p>
            <span className="font-display text-xl font-black text-primary-strong">{wordCount}</span>
          </div>
          <input
            type="range" min="5" max="40" step="1"
            value={wordCount}
            onChange={e => setWordCount(parseInt(e.target.value, 10))}
            className="w-full accent-primary"
          />
          <div className="mt-1 flex justify-between text-[10px] font-semibold text-muted-foreground">
            <span>5</span><span>40 words</span>
          </div>
          <p className="mt-3 text-xs font-semibold text-muted-foreground">
            Bloom picks from words not already in {firstName}'s other plans.
          </p>
        </div>
      )}

      {/* Edit mode info */}
      {isEdit && (
        <div className="rounded-2xl border border-border bg-muted/30 p-4">
          <p className="text-sm font-semibold text-muted-foreground">
            Next you'll review and edit the words in this plan.
          </p>
        </div>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-sm font-bold text-destructive">
          <Warning size={14} weight="fill" /> {error}
        </p>
      )}

      <BloomButton className="w-full justify-center" rightIcon={ArrowRight} onClick={goFromSettings}>
        {isEdit ? "Edit words →" : mode === "manual" ? "Choose words →" : "Set quiz length →"}
      </BloomButton>
    </div>
  );

  /* ── Step: Word Picker ──────────────────────────────────────────────── */
  const renderPicker = () => (
    <div className="flex" style={{ height: "calc(100svh - 112px)" }}>

      {/* ══ Mobile layout (< lg) ═══════════════════════════════════════════ */}
      <div className="flex h-full w-full flex-col lg:hidden">

        {/* Selected words strip */}
        <SelectedStrip
          words={selectedWords}
          total={selectedIds.size}
          onRemove={toggleWord}
          onSeeAll={() => setShowSeeAll(true)}
        />

        {/* Search + Sort */}
        <div className="shrink-0 border-b border-border px-4 py-3 space-y-2">
          <BloomInput
            icon={MagnifyingGlass}
            placeholder="Search words…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="flex justify-end">
            <SortToggle value={sort} onChange={setSort} />
          </div>
        </div>

        {/* Word list */}
        <div className="flex-1 overflow-y-auto">{renderWordList()}</div>

        {/* Footer CTA */}
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          {error && (
            <p className="mb-2 text-center text-xs font-bold text-destructive">{error}</p>
          )}
          <BloomButton
            className="w-full justify-center"
            rightIcon={selectedIds.size > 0 ? ArrowRight : undefined}
            disabled={selectedIds.size < 1}
            onClick={goFromPicker}
          >
            {selectedIds.size > 0
              ? `${selectedIds.size} word${selectedIds.size !== 1 ? "s" : ""} — Set quiz length →`
              : "Add words to continue"}
          </BloomButton>
        </div>
      </div>

      {/* ══ Web / tablet layout (≥ lg) — centred two-column ══════════════ */}
      <div className="hidden h-full w-full lg:flex lg:justify-center bg-background">
        <div className="flex h-full w-full max-w-5xl xl:max-w-6xl shadow-[0_0_0_1px_hsl(var(--border))]">

          {/* Left: scrollable word list */}
          <div className="flex flex-1 flex-col overflow-hidden bg-background">
            <div className="shrink-0 border-b border-border px-6 py-4 space-y-3">
              <BloomInput
                icon={MagnifyingGlass}
                placeholder="Search words or meanings…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div className="flex justify-end">
                <SortToggle value={sort} onChange={setSort} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">{renderWordList()}</div>
            {error && (
              <div className="shrink-0 border-t border-border px-6 py-2">
                <p className="text-xs font-bold text-destructive">{error}</p>
              </div>
            )}
          </div>

          {/* Right: selected words panel */}
          <SelectedPanel
            words={selectedWords}
            total={selectedIds.size}
            name={name}
            onRemove={toggleWord}
            onContinue={goFromPicker}
            error={error}
          />
        </div>
      </div>

      {/* Mobile see-all sheet */}
      <SeeAllSheet
        open={showSeeAll}
        words={selectedWords}
        onRemove={toggleWord}
        onClose={() => setShowSeeAll(false)}
      />
    </div>
  );

  /* ── Step: Quiz count ───────────────────────────────────────────────── */
  const renderQuiz = () => (
    <div className="space-y-6 px-4 py-6">
      {/* Summary pill */}
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/40 p-4">
        {(() => {
          const { Icon, bg, text } = iconConfig(icon);
          return (
            <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", bg)}>
              <Icon size={18} weight="fill" className={text} />
            </span>
          );
        })()}
        <div>
          <p className="font-display text-sm font-black text-foreground">{name}</p>
          <p className="text-xs font-semibold text-muted-foreground">
            {finalWordCount} words · {(isEdit || mode === "manual") ? "Manual selection" : "Auto selected"}
          </p>
        </div>
      </div>

      {/* Slider */}
      <div className="rounded-2xl border border-border bg-muted/30 p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-extrabold text-foreground">Questions per quiz session</p>
          <span className="font-display text-xl font-black text-secondary-ink">{quizCount}</span>
        </div>
        <input
          type="range"
          min={minQ}
          max={maxQ}
          step="1"
          value={Math.max(minQ, Math.min(maxQ, quizCount))}
          onChange={e => setQuizCount(parseInt(e.target.value, 10))}
          className="w-full accent-secondary"
        />
        <div className="mt-1 flex justify-between text-[10px] font-semibold text-muted-foreground">
          <span>{minQ} (each word once)</span>
          <span>{maxQ} (each word twice)</span>
        </div>
        <p className="mt-3 text-xs font-semibold text-muted-foreground">
          Struggling words always appear first in every session.
        </p>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-sm font-bold text-destructive">
          <Warning size={14} weight="fill" /> {error}
        </p>
      )}

      <BloomButton className="w-full justify-center" disabled={saving} onClick={handleSave}>
        {saving ? "Saving…" : isEdit ? `Update plan · ${finalWordCount} words` : `Save plan · ${finalWordCount} words`}
      </BloomButton>
    </div>
  );

  /* ── Page shell ─────────────────────────────────────────────────────── */
  return (
    <div className="min-h-[100svh] bg-background">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Back button */}
          <button
            onClick={goBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
            aria-label="Back"
          >
            <ArrowLeft size={16} weight="bold" />
          </button>
          {/* Left-aligned heading — matches PlansPage style */}
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-black text-foreground">{stepLabel}</p>
            <p className="truncate text-xs font-semibold text-muted-foreground">{stepDesc}</p>
          </div>
        </div>
        <div className="pb-1">
          <StepDots step={step} mode={mode} isEdit={isEdit} />
        </div>
      </div>

      {/* Content — full-width on picker step for two-column layout */}
      <div className={cn(step === "picker" ? "w-full" : "mx-auto max-w-2xl")}>
        {loadingPlan ? (
          <div className="space-y-3 px-4 py-8">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : (
          <>
            {step === "settings" && renderSettings()}
            {step === "picker"   && renderPicker()}
            {step === "quiz"     && renderQuiz()}
          </>
        )}
      </div>

      {/* Word preview modal */}
      <WordPreviewModal word={previewWord} onClose={() => setPreviewWord(null)} />
    </div>
  );
}